import * as React from 'react';
import { useCallback } from 'react';

import { Icon } from 'components/common';
import type {
  Evidence,
  EventEvidence,
  EvidenceFilters,
} from 'security-app/components/Investigations/Details/TimelineSegment/types';
import { Button } from 'components/bootstrap';
import { useModalContext } from 'common/contexts';
import { isPermitted } from 'util/PermissionsMixin';
import useCurrentUser from 'hooks/useCurrentUser';
import { removeIlluminateEventDefinitionPrefix } from 'security-app/components/Investigations/Details/TimelineSegment/Utils';

import EvidenceCardFoundation from './EvidenceCardFoundation';
import EvidenceFields from './EvidenceFields';

const EvidenceEventCard = ({
  evidence,
  onShowSimilar,
  onResetShowSimilar,
  similarFieldsFilter,
}: {
  evidence: EventEvidence;
  onResetShowSimilar: () => void;
  onShowSimilar: (targetEvidence: Evidence) => Promise<void>;
  similarFieldsFilter: EvidenceFilters['similar'];
}) => {
  const { setModal, setEntity } = useModalContext();
  const { permissions } = useCurrentUser();
  const canSeeEventDefinition = React.useMemo(
    () => isPermitted(permissions, `eventdefinitions:read:${evidence.entity.event_definition_id}`),
    [evidence, permissions],
  );

  const onReplaySearch = useCallback(() => {
    setEntity({ event: evidence.entity });
    setModal('REPLAY-SEARCH-VIEW');
  }, [evidence.entity, setEntity, setModal]);

  const { matching_fields: similarFields } = similarFieldsFilter?.fields.events.find(
    ({ event: { id } }) => id === evidence.id,
  ) ?? { matching_fields: [] };
  const subtitle = evidence.entity.message.replace(new RegExp(`^${evidence.entity.message}: `, 'i'), '');

  return (
    <EvidenceCardFoundation
      title={removeIlluminateEventDefinitionPrefix(evidence.entity.message)}
      subtitle={subtitle}
      actions={
        evidence.entity.replay_info && canSeeEventDefinition ? (
          <Button onClick={onReplaySearch} bsSize="small">
            <Icon name="play_arrow" /> Replay Search
          </Button>
        ) : null
      }
      onResetShowSimilar={onResetShowSimilar}
      hasSimilarFieldsFilter={!!similarFieldsFilter}
      evidence={evidence}
      onShowSimilar={onShowSimilar}
      isHighlighted={similarFieldsFilter?.targetEvidence.id === evidence.id}
      canSeeEventDefinition={canSeeEventDefinition}>
      {evidence.entity.fields && (
        <EvidenceFields similarFieldsFilterValue={similarFields} fields={evidence.entity.fields} />
      )}
    </EvidenceCardFoundation>
  );
};

export default EvidenceEventCard;
